import type { QuestionnaireResponseItemAnswer, Task } from 'fhir/r4';
import { stripHtml } from 'fhir-sdoh/common/constants';
import type React from 'react';
import { useState } from 'react';
import { List, Button } from 'semantic-ui-react';

import { getConsultationTaskLabelFromBundle } from './fhir-helpers';

/**
 * A Questionnaire question node as this component reads it: `linkId` and `text`
 * for the label, `answerOptions` handed straight back to `displayAnswers`.
 * Looser than `fhir4.QuestionnaireItem` because the node is read defensively
 * (`QA.text ?? QA.linkId`) and `answerOptions` is the app's own spelling.
 */
export type DiffQuestionItem = {
  answerOptions?: unknown;
  linkId?: string | undefined;
  text?: string | undefined;
};

/** The answer array as `indexQuestionnaireResponseAnswers` yields it. */
export type DiffAnswer = QuestionnaireResponseItemAnswer[] | null | undefined;

export type QuestionDiffItemProps = {
  /** Question definition from the Questionnaire (linkId, text, answerOptions, etc.) */
  QA: DiffQuestionItem;
  /** Current answers for this linkId on the addendum (or main) response */
  answer: DiffAnswer;
  diffInfo?: { oldAnswer: DiffAnswer } | undefined;
  /**
   * Renders FHIR answer values (coding, valueString, etc.).
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. The two sides disagree about
   * emptiness. This component calls it through `formatAnswerForDisplay`, whose
   * parameter is `DiffAnswer` (`… [] | null | undefined`), while the only
   * producer — `displayAnswers` in `responses.tsx` — takes a REQUIRED array and
   * immediately reads `answers[answers.length - 1]!` with no emptiness test.
   * On `[]` that indexes `-1` and throws, taking the survey panel down. The
   * `ans && ans.length > 0` guards at this component's own call sites cover the
   * null and empty cases, so the gap is only reachable if a future caller drops
   * one.
   */
  displayAnswers: (ans: DiffAnswer, opts: unknown) => React.ReactNode;
  /** Patient Task bundle; used to resolve PW-OTHER-13 task id to a label */
  tasksData?: { entry?: Array<{ resource?: Task }> } | null | undefined;
};

/**
 * Renders one survey question in addendum “chart copy” with optional before/after diff toggle.
 * PW-OTHER-13 shows the consultation task label when `tasksData` includes that Task.
 */
export default function QuestionDiffItem({ QA, answer, diffInfo, displayAnswers, tasksData }: QuestionDiffItemProps) {
  const [showDiff, setShowDiff] = useState(false);

  const formatAnswerForDisplay = (ans: DiffAnswer): React.ReactNode => {
    if (QA.linkId === 'PW-OTHER-13' && ans && ans.length > 0) {
      const id = ans[ans.length - 1]?.valueString;
      if (id) {
        const label = getConsultationTaskLabelFromBundle(id, tasksData) ?? id;
        return (
          <List.Header key={id}>
            <span style={{ whiteSpace: 'pre-wrap' }}> {label} </span>
            <List.Icon name="check" />
          </List.Header>
        );
      }
    }
    return displayAnswers(ans, QA.answerOptions);
  };

  return (
    <List.Item key={QA.linkId}>
      <List.Icon name="file alternate outline" />
      <List.Content>
        <List.Description>
          {/* Both are `0..1`. `stripHtml` assigns its argument to `innerHTML`,
              where `undefined` renders the literal text "undefined" — not the
              empty string a `?? ''` would substitute. Asserted to keep that. */}
          {stripHtml(QA.text ?? QA.linkId!)}
          {diffInfo && (
            <Button
              size="mini"
              basic
              style={{ marginLeft: '8px', padding: '4px 8px' }}
              onClick={() => setShowDiff(!showDiff)}
            >
              {showDiff ? 'Hide Previous Answer' : 'Show Previous Answer'}
            </Button>
          )}
          <div
            style={{
              marginTop: '5px',
              marginBottom: '5px',
              marginLeft: '15px',
            }}
          >
            {showDiff && diffInfo ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                {diffInfo.oldAnswer && diffInfo.oldAnswer.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: '#fff1f0',
                      borderLeft: '4px solid #ffa39e',
                      padding: '4px 8px',
                      opacity: 0.8,
                      textDecoration: 'line-through',
                    }}
                  >
                    {formatAnswerForDisplay(diffInfo.oldAnswer)}
                  </div>
                ) : (
                  <div style={{ padding: '4px 8px' }}>
                    <span style={{ color: '#888', fontStyle: 'italic' }}>No previous answer</span>
                  </div>
                )}
                <div
                  style={{
                    backgroundColor: '#f6ffed',
                    borderLeft: '4px solid #b7eb8f',
                    padding: '4px 8px',
                  }}
                >
                  {answer && answer.length > 0 ? (
                    formatAnswerForDisplay(answer)
                  ) : (
                    <span style={{ color: '#888' }}>No answer</span>
                  )}
                </div>
              </div>
            ) : (
              answer && answer.length > 0 && formatAnswerForDisplay(answer)
            )}
          </div>
        </List.Description>
      </List.Content>
    </List.Item>
  );
}
